import { useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { formatMonthLabel } from "../TenantForm/Helpers/PadInfo/padInfoTypes";

const C = { border: "#E4E9F2", muted: "#64748B", text: "#0F1F35", soft: "#F7F9FC" };

/**
 * "Notify Accounting" in its own view: what will be sent and to whom, the month's numbers
 * at a glance, the Send button, and the complete history of every notification already
 * sent for this month (previously a strip at the bottom of the report) — with the earlier
 * months kept underneath it, so the record of which months have been closed does not
 * disappear the moment the month selector moves on.
 */
const NotifyAccountingModal = ({ isOpen, toggle, month, monthLabel, updateCount, verifiedCount, notifying, notifyLog, notifyHistory, onSend }) => {
  const unverified = Math.max(0, (updateCount || 0) - (verifiedCount || 0));
  const log = notifyLog || [];

  // Everything sent for a month other than the one on screen, newest month first.
  const earlier = useMemo(() => {
    const groups = new Map();
    for (const l of notifyHistory || []) {
      if (!l?.month || l.month === month) continue;
      if (!groups.has(l.month)) groups.set(l.month, []);
      groups.get(l.month).push(l);
    }
    return [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [notifyHistory, month]);
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md" scrollable>
      <ModalHeader toggle={toggle}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <FeatherIcon icon="send" size={15} /> Notify Accounting · {monthLabel}
        </span>
      </ModalHeader>
      <ModalBody style={{ background: "#fff" }}>
        <div style={{ background: C.soft, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 12.5, color: "#334155", marginBottom: 12 }}>
          Accounting will receive an email saying the PAD updates for <strong>{monthLabel}</strong> are
          all done, with the month's Excel report attached. The company owner is copied for visibility.
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, fontSize: 12.5, fontWeight: 600 }}>
          <span style={{ background: "#EFF6FF", border: "1px solid #DBEAFE", color: "#1D4ED8", borderRadius: 999, padding: "3px 11px" }}>
            {updateCount || 0} update{updateCount === 1 ? "" : "s"}
          </span>
          <span style={{ background: "#ECFDF5", border: "1px solid #D1FAE5", color: "#0F6E56", borderRadius: 999, padding: "3px 11px" }}>
            {verifiedCount || 0} verified
          </span>
          {unverified > 0 ? (
            <span style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#B45309", borderRadius: 999, padding: "3px 11px", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <FeatherIcon icon="alert-triangle" size={12} /> {unverified} not verified yet
            </span>
          ) : null}
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <FeatherIcon icon="clock" size={13} /> Notification history · {monthLabel}
          </div>
          {log.length === 0 ? (
            <p style={{ color: "#A32D2D", fontWeight: 600, fontSize: 12.5, margin: 0 }}>
              Accounting has not been notified for this month.
            </p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#334155" }}>
              {log.map((l, i) => (
                <li key={i} style={{ margin: "3px 0" }}>
                  Notified by <strong>{l.notified_by_name || "—"}</strong>
                  {" · "}{l.at ? new Date(l.at).toLocaleString() : "—"}
                  {" · "}{l.recipients} recipient{l.recipients === 1 ? "" : "s"}
                </li>
              ))}
            </ul>
          )}
        </div>

        {earlier.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 12, paddingTop: 10 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <FeatherIcon icon="archive" size={12} /> Earlier months
            </div>
            {earlier.map(([m, entries]) => (
              <div key={m} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{formatMonthLabel(m)}</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.muted }}>
                  {entries.map((l, i) => (
                    <li key={i} style={{ margin: "2px 0" }}>
                      Notified by <strong>{l.notified_by_name || "—"}</strong>
                      {" · "}{l.at ? new Date(l.at).toLocaleString() : "—"}
                      {" · "}{l.recipients} recipient{l.recipients === 1 ? "" : "s"}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
        <Button color="primary" size="sm" onClick={onSend} disabled={notifying}>
          {notifying ? <Spinner size="sm" /> : <><FeatherIcon icon="send" size={13} className="me-1" /> Send notification</>}
        </Button>
        <Button color="light" size="sm" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export default NotifyAccountingModal;
